import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ROUTES, riskInspect } from '../../../constants/routes';
import RiskQuestionRow from '../../../components/inspection/RiskQuestionRow';

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function boolToYn(b) {
  if (b === true) return 'Y';
  if (b === false) return 'N';
  return '';
}

function ynToBool(s) {
  if (s === 'Y') return true;
  if (s === 'N') return false;
  return null;
}

function RiskInspectionForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const viewOnly =
    searchParams.get('view') === '1' || searchParams.get('view') === 'true';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [inspectorName, setInspectorName] = useState('');
  const [inspectedAtLocal, setInspectedAtLocal] = useState('');
  const [riskLevels, setRiskLevels] = useState({});
  const [locations, setLocations] = useState({});
  const [mitigations, setMitigations] = useState({});
  const [followUps, setFollowUps] = useState({});
  const [responses, setResponses] = useState({});
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);

  const readOnly = useMemo(() => {
    if (!data?.inspection) return true;
    return viewOnly || data.inspection.status === 'submitted';
  }, [data, viewOnly]);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.get(`/api/risk/inspections/${id}`, { headers: authHeader() });
      setData(res.data);
      const ins = res.data.inspection;
      setInspectorName(ins.inspectorName || '');
      setInspectedAtLocal(toDatetimeLocal(ins.inspectedAt));
      const rl = {};
      const loc = {};
      const mit = {};
      const fu = {};
      const resp = {};
      const n = {};
      for (const z of res.data.zones || []) {
        for (const g of z.groups || []) {
          for (const q of g.questions || []) {
            const a = q.answer;
            if (a) {
              rl[q.id] = a.riskLevel || '';
              loc[q.id] = a.location || '';
              mit[q.id] = boolToYn(a.mitigation);
              fu[q.id] = boolToYn(a.followUp);
              resp[q.id] = a.response || '';
              n[q.id] = a.notes || '';
            } else {
              rl[q.id] = '';
              loc[q.id] = '';
              mit[q.id] = '';
              fu[q.id] = '';
              resp[q.id] = '';
              n[q.id] = '';
            }
          }
        }
      }
      setRiskLevels(rl);
      setLocations(loc);
      setMitigations(mit);
      setFollowUps(fu);
      setResponses(resp);
      setNotes(n);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const flatQuestions = useMemo(() => {
    const list = [];
    if (!data?.zones) return list;
    for (const z of data.zones) {
      for (const g of z.groups || []) {
        for (const q of g.questions || []) {
          list.push({ zone: z.name, group: g.name, question: q });
        }
      }
    }
    return list;
  }, [data]);

  const buildAnswersPayload = () =>
    flatQuestions.map(({ question: q }) => ({
      questionId: q.id,
      riskLevel: riskLevels[q.id] || null,
      location: locations[q.id] ?? '',
      mitigation: ynToBool(mitigations[q.id]),
      followUp: ynToBool(followUps[q.id]),
      response: responses[q.id] || null,
      notes: notes[q.id] ?? '',
    }));

  const persistDraft = async () => {
    if (readOnly) return true;
    try {
      await axios.patch(
        `/api/risk/inspections/${id}`,
        {
          inspectorName,
          inspectedAt: fromDatetimeLocal(inspectedAtLocal),
          answers: buildAnswersPayload(),
        },
        { headers: authHeader() }
      );
      return true;
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Save failed');
      return false;
    }
  };

  const saveDraft = async () => {
    if (readOnly) return;
    setSaving(true);
    setError('');
    try {
      const ok = await persistDraft();
      if (ok) await load();
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (readOnly) return;
    setSaving(true);
    setError('');
    try {
      await axios.patch(
        `/api/risk/inspections/${id}`,
        {
          inspectorName,
          inspectedAt: fromDatetimeLocal(inspectedAtLocal),
          answers: buildAnswersPayload(),
        },
        { headers: authHeader() }
      );
      await axios.post(`/api/risk/inspections/${id}/submit`, {}, { headers: authHeader() });
      navigate(ROUTES.RISK_REPORTS);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Submit failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Discard this inspection? Unsaved changes may be lost.')) return;
    if (data?.inspection?.status === 'draft') {
      if (window.confirm('Delete this draft from the server?')) {
        try {
          await axios.delete(`/api/risk/inspections/${id}`, { headers: authHeader() });
        } catch (e) {
          /* still navigate */
        }
      }
    }
    navigate(ROUTES.RISK);
  };

  const uploadPhoto = async (answerId, file) => {
    if (!file || readOnly) return;
    const fd = new FormData();
    fd.append('image', file);
    try {
      await axios.post(`/api/risk/inspections/${id}/answers/${answerId}/images`, fd, {
        headers: { ...authHeader() },
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Upload failed');
    }
  };

  const deletePhoto = async (imageId) => {
    if (readOnly) return;
    try {
      await axios.delete(`/api/risk/inspections/${id}/images/${imageId}`, {
        headers: authHeader(),
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Delete failed');
    }
  };

  const openServiceRequest = async (q) => {
    setSaving(true);
    setError('');
    try {
      const ok = await persistDraft();
      if (!ok) return;
      const text = [q.questionText, notes[q.id]].filter(Boolean).join(' — ');
      const params = new URLSearchParams();
      params.set('propertyId', String(data.inspection.propertyId));
      params.set('problem', text.slice(0, 2000));
      params.set('returnTo', riskInspect(id));
      navigate(`${ROUTES.SERVICE_REQUESTS_NEW}?${params.toString()}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="app-loading text-muted" role="status">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        <span>Loading inspection…</span>
      </div>
    );
  }
  if (error && !data) return <div className="alert alert-danger">{error}</div>;
  if (!data) return null;

  return (
    <div className="app-shell-page">
      {error && <div className="alert alert-warning py-2 mb-2">{error}</div>}
      {readOnly && (
        <div className="alert alert-info py-2 mb-3">View only (submitted or read-only mode).</div>
      )}

      <div className="card app-card mb-3">
        <div className="card-body row g-3">
          <div className="col-md-6">
            <label className="form-label">Inspector name</label>
            <input
              type="text"
              className="form-control"
              value={inspectorName}
              onChange={(e) => setInspectorName(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Inspection date & time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={inspectedAtLocal}
              onChange={(e) => setInspectedAtLocal(e.target.value)}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>

      {data.zones.map((z) => (
        <div key={z.id} className="mb-4">
          <h2 className="h5 text-primary border-bottom pb-1">{z.name}</h2>
          {z.groups.map((g) => (
            <div key={g.id} className="ms-2 mb-3">
              <h3 className="h6 text-secondary mt-2">{g.name}</h3>
              <div className="app-table-wrap inspection-question-sheet">
                <div className="inspection-question-sheet-header inspection-question-sheet-header--risk d-none d-md-grid">
                  <div>Condition</div>
                  <div>Risk</div>
                  <div>Location</div>
                  <div>Mitigation</div>
                  <div>Follow-up</div>
                  <div>Response</div>
                  <div>Notes</div>
                  <div>Photo</div>
                  <div>SR</div>
                </div>
                {g.questions.map((q) => (
                  <RiskQuestionRow
                    key={q.id}
                    question={q}
                    answer={q.answer}
                    riskLevel={riskLevels[q.id]}
                    location={locations[q.id]}
                    mitigation={mitigations[q.id]}
                    followUp={followUps[q.id]}
                    response={responses[q.id]}
                    notes={notes[q.id]}
                    readOnly={readOnly}
                    onRiskLevelChange={(qid, value) =>
                      setRiskLevels((prev) => ({ ...prev, [qid]: value }))
                    }
                    onLocationChange={(qid, value) =>
                      setLocations((prev) => ({ ...prev, [qid]: value }))
                    }
                    onMitigationChange={(qid, value) =>
                      setMitigations((prev) => ({ ...prev, [qid]: value }))
                    }
                    onFollowUpChange={(qid, value) =>
                      setFollowUps((prev) => ({ ...prev, [qid]: value }))
                    }
                    onResponseChange={(qid, value) =>
                      setResponses((prev) => ({ ...prev, [qid]: value }))
                    }
                    onNotesChange={(qid, value) =>
                      setNotes((prev) => ({ ...prev, [qid]: value }))
                    }
                    onUploadPhoto={uploadPhoto}
                    onDeletePhoto={deletePhoto}
                    onCreateServiceRequest={openServiceRequest}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      {!readOnly && (
        <div className="d-flex flex-wrap gap-2 mb-4">
          <button type="button" className="btn btn-secondary" disabled={saving} onClick={saveDraft}>
            Save draft
          </button>
          <button type="button" className="btn btn-success" disabled={saving} onClick={submit}>
            Submit
          </button>
          <button type="button" className="btn btn-outline-danger" disabled={saving} onClick={cancel}>
            Cancel
          </button>
        </div>
      )}

      {readOnly && (
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => navigate(ROUTES.RISK_REPORTS)}
        >
          Back to reports
        </button>
      )}
    </div>
  );
}

export default RiskInspectionForm;
